import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionLabel } from "./Reveal";
import { EASE } from "./data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", property_name: "", property_address: "", property_type: "", spaces: "", interest: "", message: "" };
const TYPES = ["Apartment community", "HOA", "Retail / shopping center", "Office / business", "Other"];
const INTERESTS = ["Patrol & enforcement", "Pay 2 Park", "Both"];

const inputCls = "w-full border-0 border-b border-[var(--line)] bg-transparent px-0 py-3 text-base text-[var(--bone)] placeholder:text-zinc-500 transition-colors duration-300 focus:border-[var(--signal)] focus:outline-none";

const Field = ({ label, name, value, onChange, required, type = "text", ...rest }) => (
  <label className="block">
    <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">{label}{required && <span className="text-[var(--signal)]"> *</span>}</span>
    <input data-testid={`quote-input-${name}`} name={name} type={type} value={value} onChange={onChange} required={required} className={inputCls} {...rest} />
  </label>
);

export const QuoteForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/inquiries`, form);
      setStatus("sent");
      setForm(EMPTY);
      toast.success("Request sent — we'll be in touch shortly.");
    } catch (err) {
      setStatus("idle");
      toast.error(err?.response?.data?.detail && typeof err.response.data.detail === "string" ? err.response.data.detail : "Couldn't send right now. Please call 980-251-4838.");
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="relative border-t border-[var(--line)]">
      <div className="mx-auto grid max-w-[1400px] gap-14 px-5 py-24 sm:px-8 lg:grid-cols-12 lg:py-32">
        <Reveal className="lg:col-span-5">
          <SectionLabel index="04">Patrol quote</SectionLabel>
          <h2 className="font-display text-5xl font-black uppercase leading-[0.9] sm:text-6xl">Put your lot<br /><span className="text-[var(--signal)]">on patrol.</span></h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-zinc-300">Tell us about your property and we'll get back to you with a patrol plan. Prefer to talk? Call dispatch anytime.</p>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-7">
          <form onSubmit={onSubmit} data-testid="quote-form" className="grid gap-x-8 gap-y-7 sm:grid-cols-2">
            <Field label="Your name" name="name" value={form.name} onChange={onChange} required autoComplete="name" />
            <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={onChange} required autoComplete="tel" />
            <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required autoComplete="email" />
            <Field label="Property name" name="property_name" value={form.property_name} onChange={onChange} required />
            <div className="sm:col-span-2">
              <Field label="Property address" name="property_address" value={form.property_address} onChange={onChange} />
            </div>
            <label className="block">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">Property type</span>
              <select data-testid="quote-input-property_type" name="property_type" value={form.property_type} onChange={onChange} className={`${inputCls} [&>option]:bg-[var(--ink)]`}>
                <option value="">Select…</option>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <Field label="Approx. parking spaces" name="spaces" inputMode="numeric" value={form.spaces} onChange={onChange} />
            <div className="block sm:col-span-2">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">Interested in</span>
              <div className="mt-3 flex flex-wrap gap-2">
                {INTERESTS.map((t) => (
                  <button type="button" key={t} data-testid={`quote-interest-${t.split(" ")[0].toLowerCase()}`} onClick={() => setForm((f) => ({ ...f, interest: f.interest === t ? "" : t }))}
                    className={`px-4 py-2.5 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${form.interest === t ? "bg-[var(--signal)] text-[var(--ink)]" : "border border-[var(--line)] text-zinc-300 hover:text-white"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <label className="block sm:col-span-2">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">What's going on?</span>
              <textarea data-testid="quote-input-message" name="message" rows={3} value={form.message} onChange={onChange} className={`${inputCls} resize-none`} placeholder="Fire lanes, resident-only spots, abandoned cars…" />
            </label>
            <div className="flex flex-wrap items-center gap-5 sm:col-span-2">
              <motion.button whileTap={{ scale: 0.97 }} type="submit" disabled={status === "sending"} data-testid="quote-submit-btn"
                className="group flex min-h-[56px] items-center gap-3 bg-[var(--signal)] px-8 font-display text-xl font-extrabold uppercase text-[var(--ink)] transition-transform duration-300 hover:-translate-y-1 disabled:opacity-70">
                {status === "sending" ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                {status === "sending" ? "Sending" : "Request a quote"}
                {status !== "sending" && <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />}
              </motion.button>
              {status === "sent" && (
                <motion.p initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ ease: EASE }} data-testid="quote-success" className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-zinc-300">
                  <Check className="h-4 w-4 text-[var(--signal)]" /> Request received
                </motion.p>
              )}
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
